import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {Authentication, LoginRequest, SignUpRequest, SignUpResponse} from '../authentication.model';
import {endpoints} from '../../../core/configs/endpoints';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationApiService {
  private readonly _httpClient = inject(HttpClient);

  /**
   * Logs in a user with the provided credentials.
   * @param request - The login request containing the user's credentials.
   * @param lang
   * @returns An Observable emitting the authentication details upon successful login.
   */
  public login(request: LoginRequest, lang: string | null): Observable<Authentication> {
    return this._httpClient.post(endpoints.user.login, {
      login: request.login,
      password: request.password,
      lang: lang
    })
      .pipe(
        map((res: any) => {
          if (res.result.error) {
            throw new Error(res.result.error.message);
          }
          return res.result as Authentication;
        })
      )
  }

  public refreshSession(refreshSession: string): Observable<Authentication> {
    const headers = new HttpHeaders({
      'X-Openerp-Session-Id': refreshSession
    });
    return this._httpClient.get<Authentication>(endpoints.user.info, {headers});
  }

  public userInfo(userId: string, value: number): Observable<any> {
    const params = new HttpParams().set(userId, value);
    return this._httpClient.get(endpoints.user.info, {params});
  }

  /**
   * Signs up a new user with the provided registration details.
   * @param request - The sign-up request containing user registration information.
   * @returns An Observable emitting the authentication details upon successful registration.
   */
  public signUp(request: SignUpRequest): Observable<SignUpResponse> {
    return this._httpClient.post<SignUpResponse>(`${endpoints.user.signup}`, request).pipe(
      map((res: any) => {
        if (res && !res.result.error) {
          return {
            result: res.result,
            error: null
          };
        } else {
          throw new Error(res.result.error.message);
        }
      })
    );
  }

  /**
   * Logs out a user. Optionally allows for specifying a user ID to delete user-specific data.
   * @returns An Observable emitting void upon successful logout.
   */
  public logout(): Observable<any> {
    return this._httpClient.post(endpoints.user.logout, {});
  }

  verifyOtp(otp: string, email: string): Observable<any> {
    return this._httpClient.post<any>(`${endpoints.user.otp.verify}`, {params: {otp, email}}).pipe(
      map((res: any) => {
        if (res && !res.result.error) {
          return {
            result: res.result,
            error: null
          };
        } else {
          throw new Error(res.result.error.message);
        }
      })
    );
  }

  resendOtp(email: string): Observable<any> {
    return this._httpClient.post<any>(`${endpoints.user.otp.resend}`, {params: {email}}).pipe(
      map((res: any) => {
        if (res && !res.error) {
          return {
            result: res.result,
            error: null
          };
        } else {
          throw new Error(res.error.data.message);
        }
      })
    );
  }

  changePassword(otp: string, email: string, password: string, confirmPassword: string ): Observable<any> {
    return this._httpClient.post<any>(`${endpoints.user.change_password}`, {params: {otp, email, password, confirmPassword}}).pipe(
      map((res: any) => {
        if (res && !res.error) {
          return {
            result: res.result,
            error: null
          };
        } else {
          throw new Error(res.error.data.message);
        }
      })
    );
  }

}

